import { useState } from 'react';
import { useSelector } from "react-redux";
import { Button, Grid, Tab, Tabs } from "@material-ui/core";
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import FileUpload from "../../../../components/FileUpload";
import { Dropdown } from "../../../../app/components/Dropdown";
import { useTypedDispatch } from "../../../../app/state";
import { selectImportCountry, selectImportFile, setImportType } from "../../../../store/import/importState";
import {
  importCountryList,
  importCurrentFile,
  importCurrentlySelectedCountry,
  selectImportFileStatus,
  selectImportStatus,
  selectImportType,
} from "../../../../store/import/importSelectors";
import { getImportState, uploadImportFile } from "../../../../store/import/importRequests";
import { Toolbar } from "../../../../app/components";
import { ImportTypeEnum } from "../../../../models/enums/ImportType.enum";

import styles from './index.module.scss';

const ImportTopPanel = ({ children }) => {
  const [ currentFile, setCurrentFile ] = useState('');
  const dispatch = useTypedDispatch();
  const currentImportType = useSelector(selectImportType);
  const currentFileName = useSelector(importCurrentFile);
  const countryList = useSelector(importCountryList);
  const currentCountry = useSelector(importCurrentlySelectedCountry);
  const uploadStatus = useSelector(selectImportFileStatus);
  const status = useSelector(selectImportStatus) || '';

  const countryNames = countryList.map((country) => {
    return {
      name: country.name,
      id: country.id,
    }
  });

  // to use file here
  const handleFile = (file) => {
    setCurrentFile(file);
    dispatch(selectImportFile(file.name));
  }
  const handleCountryChange = (e) => {
    const countryId = e.target.value;

    const countryToSelect = countryList.find((country) => country.id === countryId);
    dispatch(selectImportCountry(countryToSelect));
  };

  const onUploadClick = (e) => {
    e.stopPropagation();
    const formData = new FormData();

    // Logic for DEVICE and LICENCE (both require Country)
    if (currentImportType === ImportTypeEnum.DEVICE || currentImportType === ImportTypeEnum.LICENCE) {
      if (currentFile && currentCountry) {
        formData.append('importFile', currentFile);
        dispatch(uploadImportFile({ 
          formData,
          countryId: currentCountry.id, 
          importType: currentImportType  // Dynamically use DEVICE or LICENCE
        }))
        .finally(() => {
          dispatch(getImportState());
        });
      }
    }

    if (currentImportType === ImportTypeEnum.USER) {
      if (currentFile) {
        formData.append('importFile', currentFile);
        dispatch(uploadImportFile({ 
          formData,
          importType: ImportTypeEnum.USER
        }))
        .finally(() => {
          dispatch(getImportState());
        });
      }
    }

    if (currentImportType === ImportTypeEnum.TECHNOLOGY) {
      if (currentFile) {
        formData.append('importFile', currentFile);
        dispatch(uploadImportFile({ 
          formData, 
          importType: ImportTypeEnum.TECHNOLOGY
        }))
        .finally(() => {
          dispatch(getImportState());
        });
      }
    }

  }

  const onTypeChange = (e, value) => {
    dispatch(setImportType(value));
  }

  // Disable if country is missing for DEVICE or LICENCE types
  const isCountryRequired = [ImportTypeEnum.DEVICE, ImportTypeEnum.LICENCE].includes(currentImportType);

  const uploadButtonDisabled = isCountryRequired ? (!currentFile || !currentCountry) : (!currentFile);

  return (
    <>
      <Toolbar title='Import tool'>
        <Grid container justifyContent="flex-end">
          <Tabs
            value={currentImportType}
            onChange={onTypeChange}
            indicatorColor='primary'
          >
            <Tab
              label={'Device'}
              value={ImportTypeEnum.DEVICE}
              disabled={!!status}
            />
            <Tab
              label={'User'}
              value={ImportTypeEnum.USER}
              disabled={!!status}
            />
            <Tab
              label={'Technology'}
              value={ImportTypeEnum.TECHNOLOGY}
              disabled={!!status}
            />
            <Tab
              label={'Licence'}
              value={ImportTypeEnum.LICENCE}
              disabled={!!status}
            />
          </Tabs>
        </Grid>
      </Toolbar>
      <Grid container spacing={2} className={styles.panel}>
        {/* Render Dropdown for both Device and Licence tabs */}
        {
          (currentImportType === ImportTypeEnum.DEVICE || currentImportType === ImportTypeEnum.LICENCE) ? (
            <Grid item xs={2}>
              <Dropdown
                disabled={!!status}
                valueChange={handleCountryChange}
                selectedValue={currentCountry?.id || 0}
                values={countryNames}
                label='Country'
                className={styles.countrySelect}
              />
            </Grid>
          ) : null
        }
        <Grid item xs={(currentImportType === ImportTypeEnum.DEVICE || currentImportType === ImportTypeEnum.LICENCE) ? 4 : 6}>
          <FileUpload
            disabled={!!status}
            handleFile={handleFile}
            fullWidth
            accept={'.csv'}
            value={currentFileName}
            placeholder={'CSV file import'}
            icon={<FolderOpenIcon />}
          />
        </Grid>
        <Grid item xs={2} className={styles.uploadButtonContainer}>
          <Button
            disabled={uploadButtonDisabled}
            variant='contained'
            color={uploadStatus.loading ? 'secondary' : 'primary'}
            fullWidth
            onClick={onUploadClick}
          >
            {uploadStatus.loading ?
              'Stop upload' :
              'Upload file'
            }
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Grid container justifyContent='center'>
            {
              children
            }
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default ImportTopPanel;